/**
 * 权限检查工具函数
 */

/** 超级管理员通配符 — 匹配所有权限 */
const SUPER_ADMIN_WILDCARD = '*';

/**
 * 检查菜单权限
 *
 * 编码使用冒号分隔层级（与 SDK PermissionConfigCompiler 一致）:
 * - `*` 匹配所有权限
 * - `admin:*` 匹配 admin 下的所有子权限
 * - `admin:topic` 精确匹配
 *
 * @param userMenus - 用户拥有的菜单权限列表
 * @param code - 要检查的权限 code
 * @returns 是否有权限
 *
 * @example
 * ```ts
 * const menus = ['admin:system:user', 'admin:system:role', 'dashboard'];
 * checkMenuPermission(menus, 'admin:system:user') // true
 * checkMenuPermission(menus, 'admin:*') // true (有 admin 下的子权限)
 * checkMenuPermission(menus, 'settings') // false
 * ```
 */
export function checkMenuPermission(
  userMenus: string[] | undefined | null,
  code: string,
): boolean {
  if (!userMenus?.length || !code) return false;

  // 精确匹配
  if (userMenus.includes(code)) return true;

  // 通配符匹配: 检查 code 是否为 'xxx:*' 模式
  if (code.endsWith(':*')) {
    const prefix = code.slice(0, -2); // 去掉 ':*'
    return userMenus.some(
      (menu) => menu === prefix || menu.startsWith(`${prefix}:`),
    );
  }

  // 检查用户菜单中是否有通配符覆盖当前 code
  return userMenus.some((menu) => {
    if (menu === SUPER_ADMIN_WILDCARD) return true;
    if (menu.endsWith(':*')) {
      const prefix = menu.slice(0, -2);
      return code === prefix || code.startsWith(`${prefix}:`);
    }
    return false;
  });
}

/**
 * 检查按钮权限
 *
 * @param userButtons - 用户拥有的按钮权限列表
 * @param code - 要检查的按钮权限 code
 * @returns 是否有权限
 *
 * @example
 * ```ts
 * const buttons = ['user:create', 'user:edit', 'role:view'];
 * checkButtonPermission(buttons, 'user:create') // true
 * checkButtonPermission(buttons, 'user:delete') // false
 * ```
 */
export function checkButtonPermission(
  userButtons: string[] | undefined | null,
  code: string,
): boolean {
  if (!userButtons?.length || !code) return false;
  if (userButtons.includes(SUPER_ADMIN_WILDCARD)) return true;
  return userButtons.includes(code);
}

/**
 * 批量检查多个权限 (全部满足)
 *
 * @param userPermissions - 用户权限列表
 * @param codes - 要检查的权限列表
 * @returns 是否全部有权限
 */
export function checkAllPermissions(
  userPermissions: string[] | undefined | null,
  codes: string[],
): boolean {
  if (!codes.length) return true;
  return codes.every((code) => checkMenuPermission(userPermissions, code));
}

/**
 * 批量检查多个权限 (任一满足)
 *
 * @param userPermissions - 用户权限列表
 * @param codes - 要检查的权限列表
 * @returns 是否有任一权限
 */
export function checkAnyPermission(
  userPermissions: string[] | undefined | null,
  codes: string[],
): boolean {
  if (!codes.length) return true;
  return codes.some((code) => checkMenuPermission(userPermissions, code));
}
